import React, { useMemo } from 'react'
import Skeleton from '@mui/material/Skeleton'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import { ListMagnifyingGlass } from '@phosphor-icons/react'
import RbacAuditActionChip from '../components/history/RbacAuditActionChip'
import RbacAuditActorCell from '../components/history/RbacAuditActorCell'
import RbacAuditDateCell from '../components/history/RbacAuditDateCell'
import RbacAuditDetailCell from '../components/history/RbacAuditDetailCell'
import RbacAuditHistoryTabShell from '../components/history/RbacAuditHistoryTabShell'
import { getActionChipConfig } from '../components/history/rbacAuditHistoryHelpers'
import {
  RBAC_USER_DETAIL_TEXTS,
  rbacUserDetailTitleSx
} from './shared/rbacUserDetailConstants'
import { skeletonBarSx } from '../components/history/rbacAuditHistoryConstants'

const SKELETON_COLUMN_WIDTHS = [
  { date: '58%', actor: '72%', action: '64%', summary: '88%' },
  { date: '46%', actor: '80%', action: '58%', summary: '76%' },
  { date: '62%', actor: '68%', action: '70%', summary: '92%' },
  { date: '52%', actor: '74%', action: '56%', summary: '82%' }
]

const historyEmptyState = {
  title: 'Aún no hay ajustes registrados',
  description: 'Los cambios realizados a este usuario aparecerán aquí.',
  icon: <ListMagnifyingGlass size={34} weight="regular" />
}

function UserHistorySkeletonRows({ rowCount = 10 }) {
  const count = Math.max(Number(rowCount) || 10, 1)

  return Array.from({ length: count }, (_, index) => {
    const widths = SKELETON_COLUMN_WIDTHS[index % SKELETON_COLUMN_WIDTHS.length]

    return (
      <TableRow key={`user-history-skeleton-${index}`} hover={false}>
        <TableCell>
          <Skeleton animation="wave" variant="rounded" height={16} width={widths.date} sx={skeletonBarSx} />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" variant="rounded" height={16} width={widths.actor} sx={skeletonBarSx} />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" variant="rounded" height={16} width={widths.action} sx={skeletonBarSx} />
        </TableCell>
        <TableCell>
          <Skeleton animation="wave" variant="rounded" height={16} width={widths.summary} sx={skeletonBarSx} />
        </TableCell>
      </TableRow>
    )
  })
}

export default function RbacUserHistoryTab({
  rows,
  loading,
  pagination,
  onPageChange,
  onRowsPerPageChange
}) {
  const columns = useMemo(
    () => [
      {
        key: 'created_at',
        label: 'FECHA Y HORA',
        width: '18%',
        cellSx: { verticalAlign: 'top' },
        render: (entry) => <RbacAuditDateCell createdAt={entry.created_at} />
      },
      {
        key: 'actor',
        label: 'USUARIO',
        width: '22%',
        cellSx: { verticalAlign: 'top' },
        render: (entry) => <RbacAuditActorCell entry={entry} />
      },
      {
        key: 'action',
        label: 'ACCIÓN',
        width: '20%',
        cellSx: { verticalAlign: 'top' },
        getTitle: (entry) => getActionChipConfig('user', entry.action).label,
        render: (entry) => <RbacAuditActionChip entry={entry} context="user" />
      },
      {
        key: 'detail',
        label: 'DETALLE',
        width: '40%',
        truncate: false,
        cellSx: { verticalAlign: 'top' },
        getTitle: (entry) => entry.summary || '',
        render: (entry) => <RbacAuditDetailCell entry={entry} context="user" />
      }
    ],
    []
  )

  return (
    <RbacAuditHistoryTabShell
      subtitle={RBAC_USER_DETAIL_TEXTS.historySubtitle}
      subtitleSx={rbacUserDetailTitleSx}
      ariaLabel="Historial de usuario RBAC"
      columns={columns}
      rows={rows}
      loading={loading}
      renderSkeletonRows={() => <UserHistorySkeletonRows rowCount={pagination.per_page} />}
      emptyState={historyEmptyState}
      pagination={pagination}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      paginationAriaLabel="Paginación de historial de usuario RBAC"
    />
  )
}
